'use client';

import { getEloCategories } from '@/api/elo/eloApi';
import { deleteRegistration } from '@/api/registrations/registrationsApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import type { Registration, Team } from '@/types';
import {
  RegistrationStatus,
  SortOption,
  type FilterOption,
  type PaymentType,
} from '@/types/tournament.model';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EloCategory } from '../models/elo.model';
import { CollapsibleCard } from './CollapsibleCard';
import { useSummaryCollapse } from './useSummaryCollapse';
import { ActiveFilters } from './participants/ActiveFilters';
import { DeleteTeamDialog } from './participants/DeleteTeamDialog';
import { EmptyState } from './participants/EmptyState';
import { RegistrationCard } from './participants/RegistrationCard';
import { SearchAndFilters } from './participants/SearchAndFilters';
import { TeamCard } from './participants/TeamCard';
import {
  formatWhatsAppNumber,
  getPlayerInitials,
  getTeamAverageElo,
  getTeamGender,
} from './participants/utils';
import { handleGlobalError } from '@/utils/error-handler';
import { ACTIVE_TOURNAMENT_STATUSES } from '@/utils/tournament-status';
import { isActiveRegistration } from '@/utils/registration-status';
import { UserPlus } from 'lucide-react';
import { RegisterPairModal } from './participants/RegisterPairModal';
import { ReplacePlayerModal } from './participants/ReplacePlayerModal';
import { EditAvailabilityModal } from './participants/EditAvailabilityModal';
import { hasGroupDaySlots } from './participants/AvailabilityPicker';
import type { KnockoutStart } from '@/utils/tournament-schedule';
import type { FinalDayRule } from './participants/final-day-rule';
import { ConfirmActionDialog } from '@/components/ui/confirm-action-dialog';
import {
  PLAYER_PAYMENT_ACTIONS,
  type PlayerPaymentAction,
} from './participants/player-payment-actions';
import { TournamentStatus } from '@/types/tournament.model';
import type { Availability } from '@/types/tournament.model';
import type { Player } from '@/types';
import { useCategoryChange } from '@/hooks/useCategoryChange';
import { ChangeCategorySheet } from './participants/ChangeCategorySheet';

interface TournamentDetailsParticipantsCardProps {
  teams: Team[] | undefined;
  registrations: Registration[] | undefined;
  sportUid: string;
  /** Tournament payment method (fallback for per-registration payment label). */
  tournamentPaymentType?: PaymentType;
  hasFixture?: boolean;
  onTeamDeleted?: () => void;
  tournamentId?: string;
  tournamentStatus?: TournamentStatus;
  tournamentInPause?: boolean;
  playersPerTeam?: number;
  minAge?: number;
  maxAge?: number;
  tournamentSchedule?: Availability[];
  /** What the last day is for: forwarded to the availability pickers of both pair modals. */
  finalDayRule?: FinalDayRule;
  /** Knockout cut: slots from it onwards are always part of a pair's availability. */
  knockoutStart?: KnockoutStart | null;
  /** Refetches the tournament. Awaited by the payment flow, so a promise is welcome here. */
  onTeamRegistered?: () => void | Promise<void>;
  /** Folded away while the tournament is in progress; the fixture takes the screen. */
  shouldCollapse?: boolean;
}

type ParticipantsTab = 'confirmed' | 'registrations';

type RegistrationFilterOption =
  | 'all'
  | 'paid'
  | 'pending'
  | 'rejected'
  | 'cancelled';

const getRegistrationStatusLabel = (status: string): string => {
  switch (status) {
    case RegistrationStatus.COMPLETE:
      return 'Pagado';
    case RegistrationStatus.PENDING_PAYMENT:
      return 'Pendiente de Pago';
    case RegistrationStatus.PENDING_ADMIN_CHECK:
      return 'Pendiente Revisión';
    case RegistrationStatus.PARTIALLY_PAID:
      return 'Pago Parcial';
    case RegistrationStatus.REJECTED_BY_ADMIN:
      return 'Rechazado';
    case RegistrationStatus.CANCELLED_BY_PLAYERS_WITH_REFUND:
      return 'Cancelado por Jugadores (con Reembolso)';
    case RegistrationStatus.CANCELLED_BY_PLAYERS_WITHOUT_REFUND:
      return 'Cancelado por Jugadores (sin Reembolso)';
    case RegistrationStatus.CANCELLED:
      return 'Cancelado';
    case RegistrationStatus.CANCELLED_BY_PLAYERS_WITHOUT_PAY:
      return 'Cancelado por Jugadores (sin Pago)';
    case RegistrationStatus.CANCELLED_AUTOMATICALLY_BY_REFUND:
      return 'Cancelado Automáticamente (con Reembolso)';
    case RegistrationStatus.CANCELLED_BY_PLAYERS_WITH_REFUND_ERROR:
      return 'Cancelado por Jugadores (Error Reembolso)';
    case RegistrationStatus.CANCELLED_BY_ADMIN:
      return 'Cancelado por Administrador';
    default:
      return status;
  }
};

const getRegistrationStatusColor = (status: string): string => {
  switch (status) {
    case RegistrationStatus.COMPLETE:
      return 'bg-green-100 text-green-800';
    case RegistrationStatus.PENDING_PAYMENT:
      return 'bg-yellow-100 text-yellow-800';
    case RegistrationStatus.PENDING_ADMIN_CHECK:
      return 'bg-blue-100 text-blue-800';
    case RegistrationStatus.PARTIALLY_PAID:
      return 'bg-orange-100 text-orange-800';
    case RegistrationStatus.REJECTED_BY_ADMIN:
    case RegistrationStatus.CANCELLED_BY_PLAYERS_WITH_REFUND:
    case RegistrationStatus.CANCELLED_BY_PLAYERS_WITHOUT_REFUND:
    case RegistrationStatus.CANCELLED:
    case RegistrationStatus.CANCELLED_BY_PLAYERS_WITHOUT_PAY:
    case RegistrationStatus.CANCELLED_AUTOMATICALLY_BY_REFUND:
    case RegistrationStatus.CANCELLED_BY_PLAYERS_WITH_REFUND_ERROR:
    case RegistrationStatus.CANCELLED_BY_ADMIN:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// When a team has more than one registration, the confirmed tab acts on the active one (COMPLETE
// first): a cancelled registration listed earlier would otherwise hide its edit actions.
const registrationRank = (status: RegistrationStatus): number => {
  if (status === RegistrationStatus.COMPLETE) return 2;
  return isActiveRegistration(status) ? 1 : 0;
};

// Same allowlist rule, for the trash button in the registrations tab: confirmed teams are
// deleted from the other tab, and the backend rejects already-cancelled registrations.
const DELETABLE_REGISTRATION_STATUSES = new Set<RegistrationStatus>([
  RegistrationStatus.PENDING_PAYMENT,
]);

const isDeletableRegistration = (status: RegistrationStatus): boolean =>
  DELETABLE_REGISTRATION_STATUSES.has(status);

// Mirrors the backend's RegistrationStatus.TEAM_STATUSES: the registrations that count as the
// tournament's teams, which is what the fixture is built from.
const TEAM_REGISTRATION_STATUSES = new Set<RegistrationStatus>([
  RegistrationStatus.PARTIALLY_PAID,
  RegistrationStatus.COMPLETE,
]);

const isTeamRegistration = (status: RegistrationStatus): boolean =>
  TEAM_REGISTRATION_STATUSES.has(status);

export function TournamentDetailsParticipantsCard({
  teams,
  registrations,
  sportUid,
  tournamentPaymentType,
  hasFixture = false,
  onTeamDeleted,
  tournamentId,
  tournamentStatus,
  tournamentInPause,
  playersPerTeam,
  minAge,
  maxAge,
  tournamentSchedule,
  finalDayRule,
  knockoutStart,
  onTeamRegistered,
  shouldCollapse = false,
}: TournamentDetailsParticipantsCardProps) {
  const [collapsed, setCollapsed] = useSummaryCollapse(shouldCollapse);
  // Lives here rather than in `Tabs`: folding the card unmounts its body, so an
  // uncontrolled Tabs would drop the manager back on "confirmados" on every reopen.
  const [activeTab, setActiveTab] = useState<ParticipantsTab>('confirmed');
  const [registerPairOpen, setRegisterPairOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<{
    registrationId: string;
    currentPlayer: Player;
    teammateEmails: string[];
  } | null>(null);

  const [availabilityTarget, setAvailabilityTarget] = useState<{
    registrationId: string;
    teamLabel: string;
    /** False until the tournament refetch lands (see openEditAvailability). */
    fresh: boolean;
  } | null>(null);

  const [paymentTarget, setPaymentTarget] = useState<{
    registrationId: string;
    player: Player;
    action: PlayerPaymentAction;
  } | null>(null);

  // Records (or undoes) a payment a player made outside the app. The backend books it through the
  // cash gateway, so the GetMatch fee lands on the manager's current account just like cash.
  // The dialog closes itself when this resolves, so the rejection has to travel: that is what keeps
  // the dialog open, with the error toast visible and the button armed for a retry.
  const handlePlayerPaymentConfirm = async (): Promise<void> => {
    if (!paymentTarget || !tournamentId) return;

    const { call, toast: copy } = PLAYER_PAYMENT_ACTIONS[paymentTarget.action];
    try {
      await call(
        tournamentId,
        paymentTarget.registrationId,
        paymentTarget.player.email,
      );
    } catch (error) {
      handleGlobalError(error, copy.errorTitle, copy.errorBody);
      throw error;
    }

    toast({
      title: copy.successTitle,
      description: copy.successBody(paymentTarget.player.name),
      variant: 'default',
    });
    // Awaited on purpose: until the refetch lands the row still reads "Pago pendiente" with the
    // button enabled, and a second click books a second payment — which is exactly what confirms
    // the registration with the teammate's half still unpaid.
    await onTeamRegistered?.();
  };

  const paymentDialogCopy = paymentTarget
    ? PLAYER_PAYMENT_ACTIONS[paymentTarget.action].dialog
    : null;

  // Builds the per-player payment handlers for a registration.
  const makePaymentHandler =
    (registration: Registration, action: PlayerPaymentAction) =>
    (player: Player) =>
      setPaymentTarget({
        registrationId: registration.id,
        player,
        action,
      });

  // Builds the per-player "replace" handler for a registration; shared by both tabs.
  const makeReplaceHandler = (registration: Registration) => (player: Player) =>
    setReplaceTarget({
      registrationId: registration.id,
      currentPlayer: player,
      teammateEmails: registration.team.players
        .filter((p) => p.email !== player.email)
        .map((p) => p.email),
    });

  const canRegisterPair =
    !!tournamentId &&
    !!playersPerTeam &&
    tournamentStatus === TournamentStatus.OPEN &&
    !tournamentInPause &&
    // Generating the fixture leaves the tournament OPEN until it is activated:
    // without this, a team could be registered into an already-built fixture.
    !hasFixture;

  // Pairs often message the organizer to change their hours. The backend accepts the edit until the
  // fixture exists (TournamentRegistrationService.updateAvailabilityAsManager): once matches are
  // scheduled, changing a pair's hours would not move them. It does not look at the tournament
  // status, so a cancelled one is ruled out here. Without slots before the final day there is
  // nothing to pick (a single-day tournament), since the final day always goes in whole.
  const canEditAvailability =
    !!tournamentId &&
    tournamentStatus !== TournamentStatus.CANCELLED &&
    !hasFixture &&
    hasGroupDaySlots(tournamentSchedule ?? [], knockoutStart);

  // The save replaces the whole set, and the registration on screen is as old as the page: the
  // pair may have changed its hours in the app since. The modal ticks nothing until a refetch lands.
  const openEditAvailability = async (
    registrationId: string,
    teamLabel: string,
  ) => {
    setAvailabilityTarget({ registrationId, teamLabel, fresh: false });
    await onTeamRegistered?.();
    setAvailabilityTarget((prev) =>
      prev?.registrationId === registrationId ? { ...prev, fresh: true } : prev,
    );
  };

  const makeEditAvailabilityHandler = (
    registration: Registration | undefined,
    teamNumber: number,
  ) =>
    canEditAvailability &&
    registration &&
    isActiveRegistration(registration.status)
      ? () => openEditAvailability(registration.id, `Equipo #${teamNumber}`)
      : undefined;

  const availabilityRegistration = availabilityTarget
    ? registrations?.find((r) => r.id === availabilityTarget.registrationId)
    : undefined;

  // Same guards the backend applies (TournamentRegistrationService.markPlayerAsPaidByManager and
  // undoManagerPaymentForPlayer). Finishing a tournament leaves a pair that played owing at
  // PARTIALLY_PAID, so the status is what stops collecting there. Once the fixture exists a pair has
  // to stay in its matches: only one already in them can be charged, and a payment can only be
  // undone while the pair keeps another one (back at PENDING_PAYMENT it would drop out of them).
  const isCollectingPayments =
    !!tournamentId &&
    !!tournamentStatus &&
    ACTIVE_TOURNAMENT_STATUSES.includes(tournamentStatus);
  const canMarkPlayerPayments = (registration: Registration): boolean =>
    isCollectingPayments &&
    (!hasFixture || isTeamRegistration(registration.status));
  const canUndoPlayerPayments = (registration: Registration): boolean =>
    isCollectingPayments &&
    (!hasFixture || (registration.payedBy?.length ?? 0) > 1);
  // State for expanded teams
  const [expandedTeams, setExpandedTeams] = useState<Record<string, boolean>>(
    {},
  );
  const [expandedRegistrations, setExpandedRegistrations] = useState<
    Record<string, boolean>
  >({});

  // State for search and filters - Confirmed Participants
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState<FilterOption>('all');
  const [sortBy, setSortBy] = useState<SortOption>(SortOption.TEAM_ASC);

  // State for search and filters - All Registrations
  const [registrationSearchQuery, setRegistrationSearchQuery] = useState('');
  const [registrationStatusFilter, setRegistrationStatusFilter] =
    useState<RegistrationFilterOption>('all');
  const [registrationSortBy, setRegistrationSortBy] = useState<SortOption>(
    SortOption.TEAM_ASC,
  );
  const [eloCategories, setEloCategories] = useState<EloCategory[]>([]);
  const categoryChange = useCategoryChange({
    tournamentId,
    // Deferred until the card is opened, like the ELO categories the sheet needs.
    enabled: tournamentStatus === TournamentStatus.FINISHED && !collapsed,
    onChanged: () => onTeamRegistered?.(),
  });

  // State for delete team dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<{
    registrationId: string;
    teamNumber: number;
  } | null>(null);

  // Toggle expanded state for confirmed participants
  const toggleTeam = (teamId: string) => {
    setExpandedTeams((prev) => ({
      ...prev,
      [teamId]: !prev[teamId],
    }));
  };

  // Toggle expanded state for registrations
  const toggleRegistration = (registrationId: string) => {
    setExpandedRegistrations((prev) => ({
      ...prev,
      [registrationId]: !prev[registrationId],
    }));
  };

  // Toggle all teams expanded/collapsed
  const toggleAllTeams = () => {
    if (!teams) return;

    const allExpanded = teams.every((team) => expandedTeams[team.id]);
    const newState = !allExpanded;

    const newExpandedTeams: Record<string, boolean> = {};
    teams.forEach((team) => {
      newExpandedTeams[team.id] = newState;
    });

    setExpandedTeams(newExpandedTeams);
  };

  // Toggle all registrations expanded/collapsed
  const toggleAllRegistrations = () => {
    if (!registrations) return;

    const allExpanded = registrations.every(
      (reg) => expandedRegistrations[reg.id],
    );
    const newState = !allExpanded;

    const newExpandedRegistrations: Record<string, boolean> = {};
    registrations.forEach((reg) => {
      newExpandedRegistrations[reg.id] = newState;
    });

    setExpandedRegistrations(newExpandedRegistrations);
  };

  // Reset filters for confirmed participants
  const resetFilters = () => {
    setSearchQuery('');
    setGenderFilter('all');
    setSortBy(SortOption.TEAM_ASC);
  };

  // Reset filters for registrations
  const resetRegistrationFilters = () => {
    setRegistrationSearchQuery('');
    setRegistrationStatusFilter('all');
    setRegistrationSortBy(SortOption.TEAM_ASC);
  };

  // Filter and sort confirmed participants (teams). Skipped while the card is folded:
  // nothing renders them, and the page re-renders on every result saved in the fixture.
  const filteredAndSortedTeams = useMemo(() => {
    if (collapsed || !teams) return [];

    const teamsWithOriginalIndex = teams.map((team, originalIndex) => ({
      ...team,
      originalIndex: originalIndex + 1,
    }));

    let result = teamsWithOriginalIndex.filter((team) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = team.players.some(
        (player) =>
          player.name.toLowerCase().includes(searchLower) ||
          player.email?.toLowerCase().includes(searchLower),
      );
      return matchesSearch;
    });

    if (genderFilter !== 'all') {
      result = result.filter((team) => {
        const teamGender = getTeamGender(team);
        return teamGender === genderFilter;
      });
    }

    return result.sort((a, b) => {
      switch (sortBy) {
        case SortOption.TEAM_ASC:
          return (a.originalIndex || 0) - (b.originalIndex || 0);
        case SortOption.TEAM_DESC:
          return (b.originalIndex || 0) - (a.originalIndex || 0);
        case SortOption.ELO_ASC:
          return (
            getTeamAverageElo(a, sportUid) - getTeamAverageElo(b, sportUid)
          );
        case SortOption.ELO_DESC:
          return (
            getTeamAverageElo(b, sportUid) - getTeamAverageElo(a, sportUid)
          );
        case SortOption.NAME_ASC:
          return a.players[0]?.name.localeCompare(b.players[0]?.name || '');
        case SortOption.NAME_DESC:
          return b.players[0]?.name.localeCompare(a.players[0]?.name || '');
        default:
          return 0;
      }
    });
  }, [collapsed, teams, searchQuery, genderFilter, sortBy, sportUid]);

  const registrationByTeamId = useMemo(() => {
    const map = new Map<string, Registration>();
    (registrations ?? []).forEach((reg) => {
      const teamId = reg.team?.id;
      if (!teamId) return;

      const existing = map.get(teamId);
      if (
        !existing ||
        registrationRank(reg.status) > registrationRank(existing.status)
      ) {
        map.set(teamId, reg);
      }
    });
    return map;
  }, [registrations]);

  // Filter and sort registrations
  const filteredAndSortedRegistrations = useMemo(() => {
    if (collapsed || !registrations) return [];

    const registrationsWithIndex = registrations.map((registration, index) => ({
      ...registration,
      originalIndex: index + 1,
    }));

    const result = registrationsWithIndex.filter((registration) => {
      const searchLower = registrationSearchQuery.toLowerCase();
      const matchesSearch = registration.team.players.some(
        (player) =>
          player.name.toLowerCase().includes(searchLower) ||
          player.email?.toLowerCase().includes(searchLower),
      );

      if (!matchesSearch) return false;

      // Filter by registration status
      if (registrationStatusFilter !== 'all') {
        switch (registrationStatusFilter) {
          case 'paid':
            return registration.status === RegistrationStatus.COMPLETE;
          case 'pending':
            return [
              'PENDING_PAYMENT',
              'PENDING_ADMIN_CHECK',
              'PARTIALLY_PAID',
            ].includes(registration.status);
          case 'rejected':
            return registration.status === 'REJECTED_BY_ADMIN';
          case 'cancelled':
            return [
              'CANCELLED_BY_PLAYERS_WITH_REFUND',
              'CANCELLED_BY_PLAYERS_WITHOUT_REFUND',
              'CANCELLED',
              'CANCELLED_BY_PLAYERS_WITHOUT_PAY',
              'CANCELLED_AUTOMATICALLY_BY_REFUND',
              'CANCELLED_BY_PLAYERS_WITH_REFUND_ERROR',
              'CANCELLED_BY_ADMIN',
            ].includes(registration.status);
        }
      }

      return true;
    });

    return result.sort((a, b) => {
      // First, sort by payment status - paid registrations first
      const aIsPaid = a.status === RegistrationStatus.COMPLETE;
      const bIsPaid = b.status === RegistrationStatus.COMPLETE;

      if (aIsPaid && !bIsPaid) return -1;
      if (!aIsPaid && bIsPaid) return 1;

      // If both have the same payment status, sort by the selected criteria
      switch (registrationSortBy) {
        case SortOption.TEAM_ASC:
          return (a.originalIndex || 0) - (b.originalIndex || 0);
        case SortOption.TEAM_DESC:
          return (b.originalIndex || 0) - (a.originalIndex || 0);
        case SortOption.ELO_ASC:
          return (
            getTeamAverageElo(a.team, sportUid) -
            getTeamAverageElo(b.team, sportUid)
          );
        case SortOption.ELO_DESC:
          return (
            getTeamAverageElo(b.team, sportUid) -
            getTeamAverageElo(a.team, sportUid)
          );
        case SortOption.NAME_ASC:
          return a.team.players[0]?.name.localeCompare(
            b.team.players[0]?.name || '',
          );
        case SortOption.NAME_DESC:
          return b.team.players[0]?.name.localeCompare(
            a.team.players[0]?.name || '',
          );
        default:
          return 0;
      }
    });
  }, [
    collapsed,
    registrations,
    registrationSearchQuery,
    registrationStatusFilter,
    registrationSortBy,
    sportUid,
  ]);

  // Count registrations by status
  const registrationCounts = useMemo(() => {
    if (!registrations)
      return { total: 0, paid: 0, pending: 0, rejected: 0, cancelled: 0 };

    return registrations.reduce(
      (counts, registration) => {
        counts.total++;
        switch (registration.status) {
          case RegistrationStatus.COMPLETE:
            counts.paid++;
            break;
          case RegistrationStatus.PENDING_PAYMENT:
          case RegistrationStatus.PENDING_ADMIN_CHECK:
          case RegistrationStatus.PARTIALLY_PAID:
            counts.pending++;
            break;
          case RegistrationStatus.REJECTED_BY_ADMIN:
            counts.rejected++;
            break;
          case RegistrationStatus.CANCELLED_BY_PLAYERS_WITH_REFUND:
          case RegistrationStatus.CANCELLED_BY_PLAYERS_WITHOUT_REFUND:
          case RegistrationStatus.CANCELLED:
          case RegistrationStatus.CANCELLED_BY_PLAYERS_WITHOUT_PAY:
          case RegistrationStatus.CANCELLED_AUTOMATICALLY_BY_REFUND:
          case RegistrationStatus.CANCELLED_BY_PLAYERS_WITH_REFUND_ERROR:
          case RegistrationStatus.CANCELLED_BY_ADMIN:
            counts.cancelled++;
            break;
        }
        return counts;
      },
      { total: 0, paid: 0, pending: 0, rejected: 0, cancelled: 0 },
    );
  }, [registrations]);

  // Deferred until the card is first opened: an in-progress tournament lands with it
  // folded. The ref keeps reopening it from re-requesting a list that does not change.
  const loadedEloSportUid = useRef<string | null>(null);

  useEffect(() => {
    if (collapsed || !sportUid || loadedEloSportUid.current === sportUid) return;

    const fetchEloCategories = async () => {
      loadedEloSportUid.current = sportUid;
      try {
        const data = await getEloCategories(sportUid);
        setEloCategories(data);
      } catch (err) {
        loadedEloSportUid.current = null;
        console.error(err);
      }
    };
    fetchEloCategories();
  }, [collapsed, sportUid]);

  // Helper function to find registration ID for a team
  const getRegistrationIdForTeam = (teamId: string): string | null => {
    if (!registrations) return null;
    const registration = registrations.find(
      (reg) =>
        reg.team.id === teamId && reg.status === RegistrationStatus.COMPLETE,
    );
    return registration?.id || null;
  };

  const openDeleteDialog = (
    registrationId: string,
    teamNumber: number,
  ): void => {
    setTeamToDelete({ registrationId, teamNumber });
    setDeleteDialogOpen(true);
  };

  // The registrations tab already holds the registration id, so it calls
  // openDeleteDialog directly; only the teams tab needs this lookup.
  const handleDeleteTeamClick = (teamId: string, teamNumber: number): void => {
    const registrationId = getRegistrationIdForTeam(teamId);
    if (!registrationId) {
      toast({
        title: 'Error',
        description:
          'No se pudo encontrar la registración asociada a este equipo.',
        variant: 'destructive',
      });
      return;
    }

    openDeleteDialog(registrationId, teamNumber);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!teamToDelete) return;

    try {
      await deleteRegistration(teamToDelete.registrationId);
      toast({
        title: 'Equipo eliminado',
        description: 'El equipo ha sido eliminado exitosamente del torneo.',
        variant: 'default',
      });
      setDeleteDialogOpen(false);
      setTeamToDelete(null);
      // Call the callback to refresh tournament data
      if (onTeamDeleted) {
        onTeamDeleted();
      }
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudo eliminar el equipo',
        'Hubo un error al intentar eliminar este equipo del torneo.',
      );
    }
  };

  // Determine if delete button should be shown
  const canDeleteTeams = !hasFixture;

  const canDeleteRegistration = (status: RegistrationStatus): boolean =>
    canDeleteTeams && isDeletableRegistration(status);

  return (
    <>
      <CollapsibleCard
        title="Participantes y Registraciones"
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        actions={
          canRegisterPair ? (
            <Button onClick={() => setRegisterPairOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Registrar equipo
            </Button>
          ) : null
        }
      >
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as ParticipantsTab)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger
              value="confirmed"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <span className="sm:hidden">Participantes</span>
              <span className="hidden sm:inline">
                Participantes Confirmados
              </span>
              <Badge variant="outline">{teams?.length || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger
              value="registrations"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <span className="sm:hidden">Registraciones</span>
              <span className="hidden sm:inline">Todas las Registraciones</span>
              <Badge variant="outline">{registrations?.length || 0}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Confirmed Participants Tab */}
          <TabsContent value="confirmed" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {filteredAndSortedTeams.length} / {teams?.length || 0} Equipos
                </Badge>
              </div>
              {teams && teams.length > 0 && (
                <Button variant="outline" size="sm" onClick={toggleAllTeams}>
                  {teams.every((team) => expandedTeams[team.id])
                    ? 'Colapsar todos'
                    : 'Expandir todos'}
                </Button>
              )}
            </div>

            {/* Search and Filters for Confirmed Participants */}
            <SearchAndFilters
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              genderFilter={genderFilter}
              setGenderFilter={setGenderFilter}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />

            {/* Active Filters Display for Confirmed Participants */}
            <ActiveFilters
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              genderFilter={genderFilter}
              setGenderFilter={setGenderFilter}
              sortBy={sortBy}
              setSortBy={setSortBy}
              resetFilters={resetFilters}
            />

            {/* Confirmed Participants List */}
            {filteredAndSortedTeams.length > 0 ? (
              <div className="space-y-4">
                {filteredAndSortedTeams.map((team, index) => {
                  const reg = registrationByTeamId.get(team.id);
                  return (
                    <TeamCard
                      key={team.id}
                      team={team}
                      index={index}
                      sportUid={sportUid}
                      isExpanded={!!expandedTeams[team.id]}
                      eloCategories={eloCategories}
                      availability={reg?.availability}
                      tournamentSchedule={tournamentSchedule}
                      toggleTeam={() => toggleTeam(team.id)}
                      getTeamAverageElo={(team) =>
                        getTeamAverageElo(team, sportUid)
                      }
                      getTeamGender={getTeamGender}
                      getPlayerInitials={getPlayerInitials}
                      formatWhatsAppNumber={formatWhatsAppNumber}
                      canDelete={canDeleteTeams}
                      onDelete={() =>
                        handleDeleteTeamClick(
                          team.id,
                          team.originalIndex || index + 1,
                        )
                      }
                      onReplacePlayer={
                        tournamentId && reg && isActiveRegistration(reg.status)
                          ? makeReplaceHandler(reg)
                          : undefined
                      }
                      onEditAvailability={makeEditAvailabilityHandler(
                        reg,
                        team.originalIndex || index + 1,
                      )}
                      getCategoryChange={categoryChange.actionFor}
                    />
                  );
                })}
              </div>
            ) : (
              <EmptyState
                hasTeams={!!teams && teams.length > 0}
                resetFilters={resetFilters}
              />
            )}
          </TabsContent>

          {/* All Registrations Tab */}
          <TabsContent value="registrations" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">
                  {filteredAndSortedRegistrations.length} /{' '}
                  {registrations?.length || 0} Registraciones
                </Badge>
                <Badge className="bg-green-100 text-green-800">
                  Pagadas: {registrationCounts.paid}
                </Badge>
                <Badge className="bg-yellow-100 text-yellow-800">
                  Pendientes: {registrationCounts.pending}
                </Badge>
                {registrationCounts.rejected > 0 && (
                  <Badge className="bg-red-100 text-red-800">
                    Rechazadas: {registrationCounts.rejected}
                  </Badge>
                )}
                {registrationCounts.cancelled > 0 && (
                  <Badge className="bg-gray-100 text-gray-800">
                    Canceladas: {registrationCounts.cancelled}
                  </Badge>
                )}
              </div>
              {registrations && registrations.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleAllRegistrations}
                >
                  {registrations.every((reg) => expandedRegistrations[reg.id])
                    ? 'Colapsar todos'
                    : 'Expandir todos'}
                </Button>
              )}
            </div>

            {/* Registration Status Filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">Filtrar por estado:</span>
              <Button
                variant={
                  registrationStatusFilter === 'all' ? 'default' : 'outline'
                }
                size="sm"
                onClick={() => setRegistrationStatusFilter('all')}
              >
                Todos
              </Button>
              <Button
                variant={
                  registrationStatusFilter === 'paid' ? 'default' : 'outline'
                }
                size="sm"
                onClick={() => setRegistrationStatusFilter('paid')}
              >
                Pagados
              </Button>
              <Button
                variant={
                  registrationStatusFilter === 'pending' ? 'default' : 'outline'
                }
                size="sm"
                onClick={() => setRegistrationStatusFilter('pending')}
              >
                Pendientes
              </Button>
              <Button
                variant={
                  registrationStatusFilter === 'rejected'
                    ? 'default'
                    : 'outline'
                }
                size="sm"
                onClick={() => setRegistrationStatusFilter('rejected')}
              >
                Rechazados
              </Button>
              <Button
                variant={
                  registrationStatusFilter === 'cancelled'
                    ? 'default'
                    : 'outline'
                }
                size="sm"
                onClick={() => setRegistrationStatusFilter('cancelled')}
              >
                Cancelados
              </Button>
            </div>

            {/* Search and Sort for Registrations */}
            <SearchAndFilters
              searchQuery={registrationSearchQuery}
              setSearchQuery={setRegistrationSearchQuery}
              genderFilter="all"
              setGenderFilter={() => {}} // Not used for registrations
              sortBy={registrationSortBy}
              setSortBy={setRegistrationSortBy}
              hideGenderFilter={true}
            />

            {/* All Registrations List */}
            {filteredAndSortedRegistrations.length > 0 ? (
              <div className="space-y-4">
                {filteredAndSortedRegistrations.map((registration, index) => (
                  <RegistrationCard
                    key={registration.id}
                    registration={registration}
                    index={index}
                    sportUid={sportUid}
                    eloCategories={eloCategories}
                    tournamentPaymentType={tournamentPaymentType}
                    isExpanded={!!expandedRegistrations[registration.id]}
                    toggleRegistration={() =>
                      toggleRegistration(registration.id)
                    }
                    getTeamAverageElo={(team) =>
                      getTeamAverageElo(team, sportUid)
                    }
                    getTeamGender={getTeamGender}
                    getPlayerInitials={getPlayerInitials}
                    formatWhatsAppNumber={formatWhatsAppNumber}
                    getRegistrationStatusLabel={getRegistrationStatusLabel}
                    getRegistrationStatusColor={getRegistrationStatusColor}
                    tournamentSchedule={tournamentSchedule}
                    onReplacePlayer={
                      tournamentId && isActiveRegistration(registration.status)
                        ? makeReplaceHandler(registration)
                        : undefined
                    }
                    onMarkPlayerPaid={
                      canMarkPlayerPayments(registration)
                        ? makePaymentHandler(registration, 'mark')
                        : undefined
                    }
                    onUndoPlayerPayment={
                      canUndoPlayerPayments(registration)
                        ? makePaymentHandler(registration, 'undo')
                        : undefined
                    }
                    onEditAvailability={makeEditAvailabilityHandler(
                      registration,
                      registration.originalIndex || index + 1,
                    )}
                    canDelete={canDeleteRegistration(registration.status)}
                    onDelete={() =>
                      openDeleteDialog(
                        registration.id,
                        registration.originalIndex || index + 1,
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-500">
                  {registrations && registrations.length > 0
                    ? 'No se encontraron registraciones con los filtros aplicados'
                    : 'No hay registraciones disponibles'}
                </p>
                {registrations && registrations.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 bg-transparent"
                    onClick={resetRegistrationFilters}
                  >
                    Limpiar filtros
                  </Button>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CollapsibleCard>

      {/* Dialogs live outside the collapsible body: "Registrar equipo" sits in the
          header and can be used while the card is folded. */}
      {/* Delete Team Dialog */}
      {deleteDialogOpen && teamToDelete && (
        <DeleteTeamDialog
          isOpen={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setTeamToDelete(null);
          }}
          onDelete={handleDeleteConfirm}
          teamNumber={teamToDelete.teamNumber}
        />
      )}

      {canRegisterPair && registerPairOpen && (
        <RegisterPairModal
          isOpen
          onClose={() => setRegisterPairOpen(false)}
          tournamentId={tournamentId!}
          playersPerTeam={playersPerTeam!}
          sportUid={sportUid}
          minAge={minAge}
          maxAge={maxAge}
          tournamentSchedule={tournamentSchedule}
          finalDayRule={finalDayRule}
          knockoutStart={knockoutStart}
          registeredEmails={[
            ...(teams ?? []).flatMap((t) =>
              (t.players ?? []).map((p) => p.email),
            ),
            ...(registrations ?? []).flatMap((r) =>
              (r.team?.players ?? []).map((p) => p.email),
            ),
          ].filter((e): e is string => !!e)}
          onRegistered={() => onTeamRegistered?.()}
        />
      )}

      {replaceTarget && tournamentId && (
        <ReplacePlayerModal
          isOpen
          onClose={() => setReplaceTarget(null)}
          tournamentId={tournamentId}
          registrationId={replaceTarget.registrationId}
          currentPlayer={replaceTarget.currentPlayer}
          teammateEmails={replaceTarget.teammateEmails}
          sportUid={sportUid}
          onReplaced={() => onTeamRegistered?.()}
        />
      )}

      {availabilityTarget &&
        availabilityRegistration &&
        tournamentId &&
        tournamentSchedule && (
          <EditAvailabilityModal
            isOpen
            onClose={() => setAvailabilityTarget(null)}
            tournamentId={tournamentId}
            registrationId={availabilityRegistration.id}
            teamLabel={availabilityTarget.teamLabel}
            availability={
              availabilityTarget.fresh
                ? (availabilityRegistration.availability ?? [])
                : null
            }
            tournamentSchedule={tournamentSchedule}
            finalDayRule={finalDayRule}
          knockoutStart={knockoutStart}
            onSaved={() => onTeamRegistered?.()}
          />
        )}

      {paymentTarget && paymentDialogCopy && (
        <ConfirmActionDialog
          isOpen
          title={paymentDialogCopy.title}
          description={paymentDialogCopy.description(paymentTarget.player.name)}
          warning={paymentDialogCopy.warning}
          confirmLabel={paymentDialogCopy.confirmLabel}
          pendingLabel={paymentDialogCopy.pendingLabel}
          onClose={() => setPaymentTarget(null)}
          onConfirm={handlePlayerPaymentConfirm}
        />
      )}

      <ChangeCategorySheet
        open={categoryChange.open}
        player={categoryChange.target}
        movedTo={categoryChange.movedTo}
        categories={eloCategories}
        sportUid={sportUid}
        onClose={categoryChange.close}
        onConfirm={categoryChange.confirm}
      />
    </>
  );
}
